import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { AbstractContentComponent } from '../../../../../../abstract-content.component';
import { Context } from '@service/context.service';
import { Column } from '@shared/table/column';
import { ChopMassnahme, ChopMesskriteriumMassnahmenRegel } from '@entity/chop';
import { ChopService } from '@service/chop.service';
import { ChopRegelDialogComponent } from './chop-regel-dialog/chop-regel-dialog.component';
import { ActionButtonDirective } from '@shared/table/action-button.directive';
import { ToolbarButtonDirective } from '@shared/table/toolbar-button.directive';
import { TableComponent } from '@shared/table/table.component';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ChopMesskriteriumDetailData } from './chop-messkriterium-detail.resolve';

interface RegelRow extends ChopMesskriteriumMassnahmenRegel {
  massnahmeBezeichnung: string;
  massnahmeTacsId: string;
}

@Component({
  selector: 'app-chop-messkriterium-detail',
  templateUrl: './chop-messkriterium-detail.component.html',
  styleUrls: ['./chop-messkriterium-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TableComponent, ToolbarButtonDirective, ActionButtonDirective],
})
export class ChopMesskriteriumDetailComponent extends AbstractContentComponent implements OnInit {
  private chopService = inject(ChopService);

  private data = this.activatedRoute.snapshot.data.detail as ChopMesskriteriumDetailData;
  private messkriterium = this.data.messkriterium;
  private profil = this.data.profil;
  private leistungsgruppe = this.data.leistungsgruppe;
  private alterskategorie = this.data.alterskategorie;
  private katalog = this.data.katalog;
  private allMassnahmen: ChopMassnahme[] = this.data.allMassnahmen;
  regeln: RegelRow[] = this.mapRegeln(this.data.regeln);
  columns: Column[] = [];
  private messkriteriumId = this.messkriterium.id;

  constructor() {
    super(Context.Global);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setColumns();
    this.refreshBreadcrumbs();
  }

  protected onLanguageChange(): void {
    this.setColumns();
    this.refreshBreadcrumbs();
    this.markForCheck();
  }

  reload(): void {
    this.chopService.getRegeln(this.messkriteriumId).subscribe((regeln) => {
      this.regeln = this.mapRegeln(regeln);
      this.markForCheck();
    });
  }

  showCreateDialog(): void {
    this.openDialog()?.onClose.subscribe((result) => {
      if (result) {
        this.reload();
      }
    });
  }

  showEditDialog(regel: ChopMesskriteriumMassnahmenRegel): void {
    this.openDialog(regel)?.onClose.subscribe((result) => {
      if (result) {
        this.reload();
      }
    });
  }

  delete(regel: ChopMesskriteriumMassnahmenRegel): void {
    this.confirm({
      header: this.translate('CHOP.DELETE_REGEL_HEADER'),
      message: this.translate('CHOP.DELETE_REGEL_MESSAGE'),
      accept: () => {
        this.chopService.deleteRegel(regel.id).subscribe({
          next: () => {
            this.showSuccessMessage();
            this.reload();
          },
          error: (err) => this.showErrorMessage(undefined, err?.error?.message),
        });
      },
    });
  }

  private openDialog(regel?: ChopMesskriteriumMassnahmenRegel): DynamicDialogRef | null {
    return this.dialogService.open(ChopRegelDialogComponent, {
      modal: true,
      closable: true,
      focusOnShow: false,
      width: '700px',
      header: this.translate(regel ? 'CHOP.EDIT_REGEL' : 'CHOP.CREATE_REGEL'),
      data: {
        messkriteriumId: this.messkriteriumId,
        regel,
        allMassnahmen: this.allMassnahmen,
        profilMassnahmenIds: this.data.profilMassnahmen.map((pm) => pm.tacsCodeKreisId),
        alreadyAssignedIds: this.regeln.map((r) => r.tacsCodeKreisId),
      },
    });
  }

  private mapRegeln(regeln: ChopMesskriteriumMassnahmenRegel[]): RegelRow[] {
    const lookup = new Map(this.allMassnahmen.map((m) => [m.id, m]));
    return regeln.map((r) => {
      const massnahme = lookup.get(r.tacsCodeKreisId);
      return {
        ...r,
        massnahmeTacsId: massnahme?.tacsId ?? '',
        massnahmeBezeichnung: massnahme ? this.getLocalizedText(massnahme.bezeichnung) : '',
      };
    });
  }

  private refreshBreadcrumbs(): void {
    this.setBreadcrumbs([
      { label: this.translate('MENU.CHOP'), routerLink: ['/admin-chop'] },
      {
        label: `${this.katalog.jahr} ${this.getLocalizedText(this.katalog.bezeichnung)}`,
        routerLink: ['/admin-chop/kataloge', this.katalog.id],
      },
      {
        label: `${this.alterskategorie.code} ${this.getLocalizedText(this.alterskategorie.bezeichnung)}`,
        routerLink: ['/admin-chop/alterskategorien', this.alterskategorie.id],
      },
      {
        label: `${this.leistungsgruppe.nummer} ${this.getLocalizedText(this.leistungsgruppe.bezeichnung)}`,
        routerLink: ['/admin-chop/leistungsgruppen', this.leistungsgruppe.id],
      },
      {
        label: `${this.profil.nummer} ${this.getLocalizedText(this.profil.bezeichnung)}`,
        routerLink: ['/admin-chop/profile', this.profil.id],
      },
      { label: `${this.translate('CHOP.MESSKRITERIUM')} #${this.messkriterium.id}` },
    ]);
  }

  private setColumns(): void {
    this.columns = [
      { field: 'massnahmeTacsId', headerKey: 'CHOP.CODE', width: '200px' },
      { field: 'massnahmeBezeichnung', headerKey: 'CHOP.MASSNAHME', minWidth: '200px' },
      { field: 'maximalAnzahl', headerKey: 'CHOP.MAXIMAL_ANZAHL', width: '160px', filterType: 'integer' },
      { field: 'mindestAnzahl', headerKey: 'CHOP.MINDEST_ANZAHL', width: '160px', filterType: 'integer' },
    ];
  }
}
